import { inject, Injectable } from '@angular/core';
import { CollectionType, Query } from '@azavista/frontend-cache-lib';
import {
    AzavistaApiUtilsService,
    IAdvancedSearchRequest,
    IPagedRequest,
    IPagedResponse,
    ISearchActivityConditionsRequest,
    ISearchActivityGroupsRequest,
    ISearchActivityRegisteredParticipantsRequest,
    ISearchEventActivitiesRequest,
    ISearchEventFieldsRequest,
    ISearchEventParticipantFieldsRequest,
    ISearchEventParticipantsRequest,
    ISearchEventsRequest,
    ISearchGlobalEventFieldsRequest,
    ISearchIntegrationsRequest,
    ISearchOrganizationsRequest,
    OnsiteCheckInClientConfig,
} from '@azavista/planner-lib';
import { GetManyResult, MutationDefinitions } from '@azavista/frontend-cache-lib/lib/collection/collection';
import { ObjectValue } from '@azavista/frontend-cache-lib/lib/entity/entity';
import { OnsiteCheckInClient } from '@azavista/planner-lib';
import { AxiosInstance } from 'axios';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericCollection = CollectionType<ObjectValue, any, any, any, MutationDefinitions, string>;

export type OnsiteCheckInClientUtilGetManyResponse<
    T extends GetManyResult<ObjectValue>
> = {
    pageOptions: IPagedResponse;
} & T;

type FilterByValueType<T, V> = {
    [K in keyof T as T[K] extends V ? K : never]: T[K];
};

export type GetManyOption = Parameters<GenericCollection['getMany']>[2];

export type OnsiteCheckInClientCollectionName = keyof FilterByValueType<
    OnsiteCheckInClient,
    GenericCollection
>;

@Injectable({
    providedIn: 'root',
})
export class OnsiteCheckInClientUtilService {
    private client = inject(OnsiteCheckInClient)
    private apiUtils = inject(AzavistaApiUtilsService);

    // This record should always contain the OnsiteCheckInClient's collections name
    private collectionRequestFnRecord = {
        activitiesCollection: (req: ISearchEventActivitiesRequest) =>
            this.apiUtils.getSearchEventActivitiesPayload(req).activity_filter,
        activityConditionsCollection: (req: ISearchActivityConditionsRequest) =>
            this.apiUtils.getSearchActivityConditionsPayload(req)
                .activity_condition_filter,
        activityGroupsCollection: (req: ISearchActivityGroupsRequest) =>
            this.apiUtils.getSearchActivityGroupsPayload(req)
                .activity_group_filter,
        activityParticipantsCollection: (
            req: ISearchActivityRegisteredParticipantsRequest & { activity_id: string }
        ) =>({
                ...this.apiUtils.getSearchEventActivityRegisteredParticipantsPayload(req),
                activity_id: req.activity_id,
            }),
        badgesCollection: (req) =>
            this.apiUtils.getSearchEventBadgesPayload(req).badge_filter,
        eventBadgeFontsCollection: () => null as never,
        eventFieldsCollection: (req: ISearchEventFieldsRequest) =>
            this.apiUtils.getSearchEventFieldsPayload(req).field_filter,
        eventsCollection: (req: ISearchEventsRequest) =>
            this.apiUtils.getSearchEventsPayload(req).event_filter,
        eventDevicesCollection: () => null as never,
        globalEventFieldsCollection: (req: ISearchGlobalEventFieldsRequest) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.apiUtils.getSearchEventFieldsPayload(req as unknown as any).field_filter,
        organizationsCollection: (req: ISearchOrganizationsRequest) =>
            this.apiUtils.getSearchOrganizationsPayload(req)
                .organization_filter,
        participantFieldsCollection: (
            req: ISearchEventParticipantFieldsRequest
        ) =>
            this.apiUtils.getSearchEventParticipantFieldsPayload(req)
                .field_filter,
        participantsCollection: (req: ISearchEventParticipantsRequest) =>
            this.apiUtils.getSearchEventParticipantsPayload(req)
                .participant_filter,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        stagesCollection: () => null as never,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        scanStagesCollection: () => null as never,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        workflowsCollection: () => null as never,
        globalSettingCollection: () => null as never,
        parentSettingCollection: () => null as never,
        onsiteSettingCollection: () => null as never,
        badgesWithTemplateCollection: () => null as never,
        integrationsCollection: (req: ISearchIntegrationsRequest) =>
            this.apiUtils.getSearchIntegrationsPayload(req)
                .integration_filter,
        multiregistrationsCollection: req => this.getGenericSearchParamsFromAdvancedSearch(req)
    } satisfies {
        [collectionName in OnsiteCheckInClientCollectionName]: (
            req: IAdvancedSearchRequest
        ) => Parameters<OnsiteCheckInClient[collectionName]['getMany']>[1]['params'];
    };

    getIsHttpClientHasSameConfig({baseUrl, clientId}: Pick<OnsiteCheckInClientConfig, 'baseUrl' | 'clientId'>, httpClient: AxiosInstance) {
        const defaults = httpClient.defaults;
        const isHttpClientHasSameConfig = defaults.baseURL === baseUrl && defaults.clientId === clientId;
        return isHttpClientHasSameConfig;
    }

    async getMany<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        CollectionName extends OnsiteCheckInClientCollectionName,
        Collection extends OnsiteCheckInClient[CollectionName],
    >(
        collectionName: CollectionName,
        partitionKey: Parameters<Collection['getMany']>[0],
        req: Parameters<OnsiteCheckInClientUtilService['collectionRequestFnRecord'][CollectionName]>[0],
        options?: GetManyOption,
    ): Promise<
        OnsiteCheckInClientUtilGetManyResponse<
            Awaited<ReturnType<Collection['getMany']>>
        >
    > {
        const collection = this.client[collectionName];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const params = this.getCollectionParams(collectionName, req as any);
        const result = await collection.getMany(
            partitionKey,
            {
                ...this.createCollectionQueryWithoutParams(req),
                ...params !== null ? {params} : {}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
            options,
        );
        return {
            pageOptions: {
                limit: req.limit,
                object_count: result.totalCount,
                offset: req.offset,
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...result as any,
        };
    }

    getCollectionParams<
        CollectionName extends OnsiteCheckInClientCollectionName,
        CollectionRequestFn extends OnsiteCheckInClientUtilService['collectionRequestFnRecord'][CollectionName],
    >(collectionName: CollectionName, req: Parameters<CollectionRequestFn>[0]): ReturnType<CollectionRequestFn> {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return this.collectionRequestFnRecord[collectionName](req as any) as any;
    }

    // Common
    async getAllPagedItems<
        Req extends IPagedRequest,
        Res extends GetManyResult<{}>,
        TItem
    >(
        loadFunc: (request: Req) => Promise<Res>,
        responseArrayKeySelector: (response: Res) => TItem[],
        requestForTheLoadFunc?: Req
    ): Promise<TItem[]> {
        const result: TItem[] = [];
        const request: Req =
            requestForTheLoadFunc || ({ limit: 50, offset: 0 } as Req);
        let itemsCount = 0;
        let hasMoreItems: boolean;
        do {
            const response = await loadFunc(request);
            const responseArray = responseArrayKeySelector(response);
            itemsCount = responseArray ? responseArray.length : 0;
            if (itemsCount > 0) {
                result.push(...responseArray);
                request.offset += itemsCount;
            }
            hasMoreItems =
                itemsCount > 0 && request.offset < response.totalCount;
        } while (hasMoreItems);
        return result;
    }

    private createCollectionQueryWithoutParams<T extends IAdvancedSearchRequest>(
        advancedSearch: T
    ): Omit<Query<undefined>, 'params'> {
        const { limit, offset, sortDirection, sortFieldName } = advancedSearch;
        const result: Omit<Query<undefined>, 'params'> = {
            limit,
            offset,
        };
        if (sortFieldName) {
            result.sort = [
                {
                    field: sortFieldName,
                    order: sortDirection || 'asc',
                },
            ];
        }
        return result;
    }

    private getGenericSearchParamsFromAdvancedSearch<FieldName extends string>(req: IAdvancedSearchRequest<FieldName>) {
        const searchParams = {};
        this.apiUtils.setAdvancedQueryProps({}, searchParams, req);
        return searchParams;
    }
}
